import { Button, Text, TextArea } from '@react-spectrum/s2';
import EditIcon from '@react-spectrum/s2/icons/Edit';
import {
	type KeyboardEvent,
	useId,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

import { STAGGER_MS } from '@/utils/animationConstants';

import {
	GROUNDING_FIELDS,
	type GroundingAnswers,
	type GroundingFieldId,
	type GroundingTally,
} from './groundingFields';
import { type NorthStar } from './northStar';
import { NorthStarRow } from './NorthStarRow';

// The summary the owner reaches after answering the questions. It is the last screen, not the
// first: the walk goes 1-by-1 and this is where it lands. Every row is corrected HERE — pressing
// Edit opens a text area in place, and Save commits it without moving the step cursor. The screen
// asks the owner to fix what is wrong, so it has to accept the fix.

export function GroundingReviewStep({
	answers,
	northStar,
	northStarUnsaved,
	onConfirmNorthStar,
	onSaveAnswer,
	footerAction,
	tally,
}: {
	answers: GroundingAnswers;
	northStar: NorthStar | null;
	/** The confirmed number on screen has not reached the server. */
	northStarUnsaved?: boolean;
	onConfirmNorthStar: (star: NorthStar | null) => void;
	/** Commit one corrected answer. Does not move the step cursor. */
	onSaveAnswer: (id: GroundingFieldId, value: string) => void;
	/** Generate. Sits in the same slot the step card's primary occupies. */
	footerAction?: React.ReactNode;
	/** Counted once by the flow, so this screen cannot disagree with the row below the card. */
	tally: GroundingTally;
}) {
	return (
		<div
			data-testid="grounding-review"
			className="flex h-full min-h-0 flex-col gap-4"
		>
			<div
				className="animate-grounding-rise flex-shrink-0"
				style={{ animationDelay: '90ms' }}
			>
				<h3 className="text-lg font-semibold text-(--text-primary)">
					Interview Summary
				</h3>
			</div>

			<div
				className="scrollbar-hidden min-h-[180px] flex-1 basis-0 space-y-1
					overflow-y-auto
					[mask-image:linear-gradient(to_bottom,black_calc(100%-14px),transparent)]
					pr-1"
			>
				<NorthStarRow
					northStar={northStar}
					goalAnswer={answers.goal ?? ''}
					unsaved={northStarUnsaved}
					onConfirm={onConfirmNorthStar}
				/>

				{GROUNDING_FIELDS.map((field, i) => (
					<ReviewRow
						key={field.id}
						id={field.id}
						label={field.question}
						fieldLabel={field.label}
						value={(answers[field.id] ?? '').trim()}
						order={i}
						onSave={(value) => onSaveAnswer(field.id, value)}
					/>
				))}

				{/* Anything the parser could not place used to render here under "Your earlier
				    notes — kept as written". It read as the product having invented a section: the
				    common case was not the owner's prose at all but bullets whose question had been
				    retired, and the owner has no way to act on a block they cannot edit. The text
				    is still PRESERVED — `composeGroundingMarkdown` re-emits `extra` on every save,
				    so it stays in the brief the strategist reads. It is simply not shown back to
				    the owner as though they had written it here. */}
			</div>

			{/* The count on the left, Generate hard right. There is no button back into the walk:
			    every answer is corrected here, in place. */}
			<div className="flex flex-shrink-0 items-center gap-2 pt-4">
				<span className="text-xs text-(--text-muted) tabular-nums">
					{tally.filled} of {tally.total} filled in
				</span>
				<div className="ml-auto">{footerAction}</div>
			</div>
		</div>
	);
}

function ReviewRow({
	id,
	label,
	fieldLabel,
	value,
	order,
	onSave,
}: {
	id: GroundingFieldId;
	/** The question, shown above the answer. */
	label: string;
	/** The short name of the fact, used for the Edit button's accessible name. */
	fieldLabel: string;
	value: string;
	order: number;
	onSave: (value: string) => void;
}) {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState('');
	const labelId = useId();
	const editorRef = useRef<HTMLDivElement>(null);

	// Seed from the stored answer on OPEN, never clear on close. A cancelled draft is left behind
	// and overwritten the next time the row opens, which is how the Brand cards behave.
	const open = () => {
		setDraft(value);
		setEditing(true);
	};
	const cancel = () => setEditing(false);

	const trimmed = draft.trim();
	const canSave = trimmed !== '' && trimmed !== value;

	const save = () => {
		if (!canSave) return;
		onSave(trimmed);
		setEditing(false);
	};

	// Escape has to stop here. The review step has no handler of its own, so an untrapped Escape
	// reaches the dock and closes the whole panel — taking the correction with it.
	const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Escape') {
			e.stopPropagation();
			cancel();
		}
	};

	// The list scrolls inside a short box with a fade at its foot, so an editor opened on a low row
	// can open below the fold. jsdom has no scrollIntoView, hence the optional call.
	useLayoutEffect(() => {
		if (editing) editorRef.current?.scrollIntoView?.({ block: 'nearest' });
	}, [editing]);

	return (
		// The entrance animation and its stagger belong to the ROW, and they play once. The editor
		// below must not carry them: the utility fills backwards, so a freshly mounted node would
		// sit invisible for its whole delay, which reaches 890ms on the last row.
		<div
			data-testid="grounding-review-row"
			data-field={id}
			data-answered={value ? 'true' : 'false'}
			data-editing={editing ? 'true' : 'false'}
			style={{ animationDelay: `${90 + order * STAGGER_MS}ms` }}
			className="animate-grounding-rise group w-full rounded-lg"
		>
			{editing ? (
				<div
					ref={editorRef}
					data-testid="grounding-review-editor"
					onKeyDown={onKeyDown}
					className="space-y-3 rounded-lg border border-(--glass-border) px-2
						py-2"
				>
					<span id={labelId} className="block text-xs text-(--text-muted)">
						{label}
					</span>
					<TextArea
						size="M"
						aria-labelledby={labelId}
						value={draft}
						onChange={setDraft}
					/>
					{/* Both commit controls sit together at the right, Cancel beside Save, the way
					    the Brand cards pair them. */}
					<div className="flex items-center justify-end gap-2">
						<Button variant="secondary" onPress={cancel}>
							<Text>Cancel</Text>
						</Button>
						<Button variant="accent" isDisabled={!canSave} onPress={save}>
							<Text>Save</Text>
						</Button>
					</div>
				</div>
			) : (
				<div
					className="flex items-center justify-between gap-2 rounded-lg px-2
						py-1.5 text-left hover:bg-(--glass-bg-hover)"
				>
					<div className="min-w-0 flex-1">
						<span id={labelId} className="block text-xs text-(--text-muted)">
							{label}
						</span>
						{value ? (
							// No `block` here. `line-clamp-3` needs `display: -webkit-box`, and
							// `block` sets `display` too. Tailwind emitted `block` last, so it won
							// and a long answer rendered at full height instead of clamping.
							<span
								data-testid="grounding-review-value"
								className="line-clamp-3 text-sm text-(--text-primary)"
							>
								{value}
							</span>
						) : (
							<span
								data-testid="grounding-review-value"
								className="block text-sm text-(--text-muted) italic"
							>
								Not added
							</span>
						)}
					</div>
					{/* Hidden until the row is hovered OR something inside it takes focus. Without
					    the focus rule a keyboard user can reach a control they cannot see. */}
					<div
						className="flex-shrink-0 opacity-0 transition-opacity
							group-focus-within:opacity-100 group-hover:opacity-100"
					>
						<Button
							variant="primary"
							size="S"
							aria-label={`Edit ${fieldLabel}`}
							onPress={open}
						>
							<EditIcon />
							<Text>Edit</Text>
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}
